import { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { ApolloError } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';

import { useMetrics } from '../../appSelectorContext';
import { ERROR_CODES, METRIC_PUBLISHER_NAMES, RECEIVE_TYPES_UI } from '../../constants';
import useLocalStorage from '../../hooks/useLocalStorage';
import { Context, Types } from '../../store';
import { AppContext, AppType } from '../../store/appContext';
import { ReceiveType, ReferenceQueryResponseItem, ValidationStatusType } from '../../store/types';
import { useService } from '../../store/useService';
import { SEARCH_REFERENCES } from './StartReceivingService';
import { RMA_RECIEVE_PAYLOAD, SHIPMENT_NOT_FOUND_METRIC } from './constants';

export interface MultipleReferences {
  paginationNextToken: string;
  receiveReferenceQueryResponseList: ReferenceQueryResponseItem[];
}

/**
 * The useSearchReferences hook provides data and business logic for the useSearchReferences component

 */
export const useSearchReferences = ({ rma }: any) => {
  const { dispatch }: any = useContext(Context);
  const appContext = useContext(AppContext);
  const context = useMetrics();
  const [multipleReferencesFound, setMultipleReferencedFound] = useState<null | MultipleReferences>(null);
  const history = useHistory();
  const { useLazyQuery } = useService();
  let { modelId, referenceId }: any = useParams();
  const [preferredSite] = useLocalStorage('unifiedReceivePreferredSite');
  const siteName = preferredSite?.value;
  const siteId = preferredSite?.locationId;
  const urlParams = new URLSearchParams(window.location.search);
  const shipmentId = urlParams.get('shipmentId');
  const publisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(
    METRIC_PUBLISHER_NAMES.SCAN_SHIPMENT,
  );

  // handle if the shipmentID is in the query param
  const handleShipmentId = (references: ReferenceQueryResponseItem[]) => {
    const reference = references?.find(
      (ref: ReferenceQueryResponseItem) => ref.receiveReference.receiveReferenceId === referenceId,
    );

    if (!reference) {
      publisher.publishCounterMonitor(SHIPMENT_NOT_FOUND_METRIC, 1);
      dispatch({
        type: Types.scanReference,
        payload: {
          data: [],
          loading: false,
          errors: ['shipment_id_not_found'],
        },
      });
      history.push(`/receive/`);
      return;
    }

    dispatch({
      type: Types.scanReference,
      payload: {
        data: { receiveReferenceQueryResponseList: [reference] },
        loading,
        errors: [],
      },
    });
  };

  const [searchReferences, { loading, variables, error }] = useLazyQuery(SEARCH_REFERENCES, {
    fetchPolicy: 'network-only',
    onCompleted: async (data) => {
      let loading = false;
      const variableArgs = appContext.appType === AppType.BOOST ? data?.searchReferences?.variables : variables;
      const searchReferences: ReferenceQueryResponseItem[] =
        data?.searchReferences?.receiveReferenceQueryResponseList?.map((ref: ReferenceQueryResponseItem) => ({
          ...ref,
          scanStartTime: Date.now(),
          siteName,
          siteId,
        }));

      const validSearchReferences = searchReferences?.filter(
        (ref) => ref?.status?.validationStatusType !== ValidationStatusType.ERROR,
      );

      if (searchReferences?.length <= 0 && !rma) {
        publisher.publishCounterMonitor(SHIPMENT_NOT_FOUND_METRIC, 1);
        dispatch({
          type: Types.scanReference,
          payload: {
            data: [],
            loading,
            errors: ['shipment_id_not_found'],
          },
        });
        return;
      }

      const pos = validSearchReferences?.filter(
        (ref) => ref?.receiveReference.externalReferenceType === ReceiveType.AWS_PO,
      );

      if (referenceId && shipmentId) {
        handleShipmentId(validSearchReferences);
        history.push(
          `/receive/shipment/${searchReferences?.[0]?.receiveReference?.receiveReferenceId}?${urlParams.toString()}`,
        );
        return;
      }

      const rmaID = variableArgs?.referenceIdList?.[0]?.value;
      const rmaUUID = uuidv4();
      const rmaPayload = {
        ...RMA_RECIEVE_PAYLOAD,
        receiveReference: {
          ...RMA_RECIEVE_PAYLOAD.receiveReference,
          receiveReferenceId: rmaUUID,
          externalReferenceValue: rmaID,
          scanStartTime: Date.now(),
          siteName,
          siteId,
        },
      };
      if (validSearchReferences?.length > 1 && rma) {
        dispatch({
          type: Types.scanReference,
          payload: {
            data: { receiveReferenceQueryResponseList: [rmaPayload] },
            loading,
            errors: [],
          },
        });
        return;
      }

      if (validSearchReferences?.length > 1) {
        dispatch({
          type: Types.scanReference,
          payload: {
            data: {},
            loading,
            errors: [],
          },
        });
        setMultipleReferencedFound({
          ...data?.searchReferences,
          receiveReferenceQueryResponseList: validSearchReferences,
        });
        return;
      }

      const valid = searchReferences?.[0]?.status?.validationStatusType === ValidationStatusType.OK;

      if (valid && pos.length > 0) {
        dispatch({
          type: Types.scanReference,
          payload: {
            data: { ...data?.searchReferences, receiveReferenceQueryResponseList: pos },
            loading,
            errors: ['shipment-type-po'],
          },
        });
        return;
      }

      /* istanbul ignore next */
      if (!valid && rma) {
        dispatch({
          type: Types.scanReference,
          payload: {
            data: {
              receiveReferenceQueryResponseList: [rmaPayload],
            },
            loading,
            errors: [],
          },
        });
        history.push(`/receive/no_reference/${rmaUUID}?${urlParams.toString()}`);
        return;
      }

      if (!valid) {
        const errorCode = searchReferences?.[0]?.status?.statusCode || 'shipment_id_not_found';

        dispatch({
          type: Types.scanReference,
          payload: {
            data: [],
            loading,
            errors: [errorCode],
          },
        });
        return;
      }

      dispatch({
        type: Types.scanReference,
        payload: {
          data: { ...data?.searchReferences, receiveReferenceQueryResponseList: searchReferences },
          loading,
          errors: [],
        },
      });

      const receiveType = RECEIVE_TYPES_UI[searchReferences?.[0].scannableIdFoundAs];
      !modelId &&
        searchReferences?.[0]?.receiveReference?.receiveReferenceId &&
        receiveType &&
        history.push(
          `/receive/${receiveType}/${
            searchReferences?.[0]?.receiveReference?.receiveReferenceId
          }?${urlParams.toString()}`,
        );
    },
    onError: ({ graphQLErrors, networkError }: ApolloError) => {
      console.error('searchReferences errors', graphQLErrors, networkError, variables);
      const serverErrorCode = graphQLErrors?.[0]?.extensions?.exception?.code;
      const error = serverErrorCode || ERROR_CODES.network_error;
      dispatch({
        type: Types.scanReference,
        payload: {
          errors: [error],
          loading,
        },
      });
    },
  });
  return {
    searchReferences,
    multipleReferencesFound,
    setMultipleReferencedFound,
    request: variables,
    loading,
    error,
  };
};

export default useSearchReferences;
